import { useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2, CheckCircle2, ArrowRight, TrendingDown } from "lucide-react";
import { toast } from "sonner";

export default function SwitchDialog({ open, onOpenChange, offer, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/switch-requests", {
        category_slug: offer.category_slug,
        offer_id: offer.id,
        current_price: offer.current_price,
        recommended_price: offer.new_price,
        to_provider_name: offer.title,
      });
      setDone(true);
      if (data.earned_points) toast.success(`Switch requested — you earned ${data.earned_points} points!`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!offer) return null;
  const annual = offer.period === "month" ? offer.saving * 12 : offer.saving;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setDone(false); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="switch-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{done ? "Request submitted" : "Switch & Save"}</DialogTitle>
          <DialogDescription>{offer.title}</DialogDescription>
        </DialogHeader>

        {!done && (
          <div>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 p-4 text-center">
                <p className="text-xs uppercase font-bold text-slate-400">Current</p>
                <p className="font-display text-2xl font-extrabold text-slate-900 mt-1">{money(offer.current_price)}</p>
                <p className="text-xs text-slate-500">/{offer.period}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 shrink-0" />
              <div className="flex-1 bg-emerald-500 rounded-xl p-4 text-center text-white">
                <p className="text-xs uppercase font-bold text-emerald-50">Recommended</p>
                <p className="font-display text-2xl font-extrabold mt-1">{money(offer.new_price)}</p>
                <p className="text-xs text-emerald-50">/{offer.period}</p>
              </div>
            </div>
            <div className="mt-4 bg-amber-50 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><TrendingDown className="w-5 h-5 text-amber-600" /><span className="font-semibold text-slate-700 text-sm">Potential annual saving</span></div>
              <span className="font-display font-extrabold text-amber-700">{money(annual)}</span>
            </div>
            {offer.reward_points > 0 && <p className="text-center text-sm text-slate-500 mt-3">Earn <span className="font-bold text-emerald-600">{offer.reward_points} points</span> when you switch</p>}
            <Button onClick={submit} disabled={submitting} data-testid="switch-submit-btn" className="w-full mt-4 rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold py-6">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "SWITCH & SAVE"}
            </Button>
          </div>
        )}

        {done && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-emerald-500" /></div>
            <p className="font-display font-bold text-slate-900 mt-4">We'll be in touch</p>
            <p className="text-sm text-slate-500 mt-1">Your switch request was sent to the provider. Our team will contact you to complete it.</p>
            <Button onClick={() => onOpenChange(false)} data-testid="switch-done-btn" className="w-full mt-6 rounded-full bg-slate-900 hover:bg-slate-800 font-bold">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
